import React, { useState, useEffect } from "react";
import { NavigationItem } from "../types";

interface HeaderProps {
  activeSection: string;
  setActiveSection: (section: string) => void;
}

const Header: React.FC<HeaderProps> = ({ activeSection, setActiveSection }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [canvasRef, setCanvasRef] = useState<HTMLCanvasElement | null>(null);
  const [videoRef, setVideoRef] = useState<HTMLVideoElement | null>(null);

  // Canvas animation for Safari transparency
  useEffect(() => {
    if (!canvasRef || !videoRef) return;

    const ctx = canvasRef.getContext("2d");
    if (!ctx) return;

    const animate = () => {
      if (videoRef.readyState >= 2) {
        ctx.clearRect(0, 0, canvasRef.width, canvasRef.height);
        ctx.drawImage(videoRef, 0, 0, canvasRef.width, canvasRef.height);
      }
      requestAnimationFrame(animate);
    };

    videoRef.addEventListener("loadeddata", animate);
    return () => videoRef.removeEventListener("loadeddata", animate);
  }, [canvasRef, videoRef]);

  const navigationItems: NavigationItem[] = [
    { id: "home", label: "Home", href: "#home" },
    { id: "publications", label: "Publications", href: "#publications" },
    { id: "research", label: "AI Empathy", href: "#research" },
    { id: "engagement", label: "Engagement", href: "#engagement" },
    { id: "experience", label: "Experience", href: "#experience" },
  ];

  // Handle scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Show header when scrolling up or at top
      if (currentScrollY < lastScrollY || currentScrollY < 10) {
        setIsVisible(true);
      }
      // Hide header when scrolling down (but not if already at top)
      else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
        setMobileMenuOpen(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const handleNavClick = (sectionId: string) => {
    setActiveSection(sectionId);
    setMobileMenuOpen(false);
  };

  const toggleMobileMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        mobileMenuOpen &&
        !target.closest(".nav-menu") &&
        !target.closest(".hamburger")
      ) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [mobileMenuOpen]);

  return (
    <header
      className={`header ${isVisible ? "header-visible" : "header-hidden"}`}
    >
      <div className="header-container">
        <nav className="nav">
          {/* Logo - with blue background */}
          <div
            className="logo-container"
            onClick={() => handleNavClick("home")}
          >
            <video
              className="logo-video"
              autoPlay
              muted
              playsInline
              preload="auto"
              ref={(video) => {
                if (video) {
                  video.play().catch(() => {
                    // Autoplay failed, but that's ok - user can click
                  });
                }
              }}
            >
              <source src="/images/logo.mp4" type="video/mp4" />
            </video>
          </div>

          <ul className={`nav-menu ${mobileMenuOpen ? "active" : ""}`}>
            {navigationItems.map((item) => (
              <li key={item.id}>
                <a
                  href={item.href}
                  className={`nav-link ${
                    activeSection === item.id ? "active" : ""
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(item.id);
                  }}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>

          <div
            className="hamburger"
            onClick={toggleMobileMenu}
            role="button"
            aria-label="Toggle mobile menu"
            aria-expanded={mobileMenuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
